import React, { useEffect, useRef, useState } from 'react';
import './modernized_app.css';

const formatLocalDateTime = value => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString([], {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const toDateTimeLocalValue = value => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  const pad = number => String(number).padStart(2, '0');
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
};

const fromDateTimeLocalValue = value => {
  if (!value) return '';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString();
};

const centsToDollars = value => {
  if (value === null || value === undefined || value === '') return '';
  return (Number(value) / 100).toFixed(2);
};

const formatCurrencyFromCents = value => {
  const dollars = centsToDollars(value);
  return dollars ? `$${dollars}` : '$0.00';
};

const dollarsToCents = value => {
  const normalized = String(value ?? '').trim();
  if (!normalized) return '';
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : '';
};

const formatDeploymentMode = value => ({
  mfe_hosted_saas: 'MFE-hosted SaaS',
  customer_owned: 'Customer-owned',
  hybrid: 'Hybrid',
}[value] || value || 'MFE-hosted SaaS');

const STANDARD_INTEGRATION_FLAGS = {
  commander_ai_brief: false,
  smart_custom_report_builder: false,
  launch_success_advisor: false,
  weather: false,
  status_exports: true,
  reports_pdf_export: true,
  notifications: true,
  personnel_sms_alerts: false,
  personnel_availability_prediction: false,
  safety_forms: true,
  lost_tool: true,
  recovery_personnel_scheduling: false,
  acft_plans: true,
  map_ramp_layout: true,
  crystal_ball: false,
};

const PREMIUM_INTEGRATION_FLAGS = {
  ...Object.fromEntries(Object.keys(STANDARD_INTEGRATION_FLAGS).map(key => [key, true])),
  personnel_sms_alerts: false,
};

const INTEGRATION_FLAG_OPTIONS = [
  { key: 'commander_ai_brief', label: 'Commander AI Brief' },
  { key: 'smart_custom_report_builder', label: 'Smart Custom Report Builder' },
  { key: 'launch_success_advisor', label: 'Launch Success Advisor' },
  { key: 'weather', label: 'Weather' },
  { key: 'status_exports', label: 'Status Exports' },
  { key: 'reports_pdf_export', label: 'Reports PDF Export' },
  { key: 'notifications', label: 'Email Notifications' },
  { key: 'personnel_sms_alerts', label: 'Personnel SMS Alerts' },
  { key: 'personnel_availability_prediction', label: 'Personnel Availability Prediction' },
  { key: 'safety_forms', label: 'Safety Forms' },
  { key: 'lost_tool', label: 'Lost Tool' },
  { key: 'recovery_personnel_scheduling', label: 'Recovery Personnel Scheduling' },
  { key: 'acft_plans', label: 'ACFT Plans' },
  { key: 'map_ramp_layout', label: 'Map Ramp Layout' },
  { key: 'crystal_ball', label: 'Crystal Ball' },
];

const inferPlanFromIntegrationFlags = flags => {
  const normalized = { ...STANDARD_INTEGRATION_FLAGS, ...(flags || {}) };
  const matchesStandard = Object.entries(STANDARD_INTEGRATION_FLAGS).every(([key, value]) => Boolean(normalized[key]) === value);
  if (matchesStandard) return 'Standard';
  const matchesPremium = Object.entries(PREMIUM_INTEGRATION_FLAGS).every(([key, value]) => Boolean(normalized[key]) === value);
  if (matchesPremium) return 'Premium';
  return 'Enterprise / Custom Contract';
};

const hydrateSubscriptionDraft = row => row ? ({
  planName: row.planName || 'Standard',
  displayPlanName: row.displayPlanName || row.planName || 'Standard',
  contractPlanName: row.contractPlanName || row.planName || '',
  subscriptionStatus: row.subscriptionStatus || 'trial',
  seatLimit: row.seatLimit || 25,
  seatAddOnQuantity: row.seatAddOnQuantity || 0,
  seatAddOnSeats: row.seatAddOnSeats || 0,
  aiAddOnQuantity: row.aiAddOnQuantity || 0,
  aiAddOnReportRuns: row.aiAddOnReportRuns || 0,
  aiAddOnTokens: row.aiAddOnTokens || 0,
  billingOwnerEmail: row.billingOwnerEmail || '',
  billingContactEmail: row.billingContactEmail || '',
  billingProvider: row.billingProvider || '',
  billingPlanKey: row.billingPlanKey || '',
  billingInterval: row.billingInterval || '',
  providerCustomerId: row.providerCustomerId || '',
  providerSubscriptionId: row.providerSubscriptionId || '',
  pricePerMonth: centsToDollars(row.pricePerMonthCents),
  currency: row.currency || 'USD',
  trialEndsAt: toDateTimeLocalValue(row.trialEndsAt),
  currentPeriodEndsAt: toDateTimeLocalValue(row.currentPeriodEndsAt),
  aiEnabled: Boolean(row.aiEnabled),
  aiProvider: row.aiProvider || 'openai',
  aiKeyMode: row.aiKeyMode || 'platform',
  aiKeySecretRef: row.aiKeySecretRef || '',
  aiMonthlyRequestLimit: row.aiMonthlyRequestLimit ?? 100,
  aiMonthlyTokenLimit: row.aiMonthlyTokenLimit ?? 0,
  customerAccountName: row.customerAccountName || '',
  deploymentMode: row.deploymentMode || 'mfe_hosted_saas',
  deployedVersion: row.deployedVersion || '',
  releaseChannel: row.releaseChannel || '',
  supportContactName: row.supportContactName || '',
  supportContactEmail: row.supportContactEmail || '',
  mfeSupportAccessEnabled: Boolean(row.mfeSupportAccessEnabled),
  mfeSupportAccessGrantedBy: row.mfeSupportAccessGrantedBy || '',
  mfeSupportAccessGrantedAt: row.mfeSupportAccessGrantedAt || '',
  mfeSupportAccessExpiresAt: toDateTimeLocalValue(row.mfeSupportAccessExpiresAt),
  mfeSupportAccessReason: row.mfeSupportAccessReason || '',
  mfeOwnerSupportEmail: row.mfeOwnerSupportEmail || 'info@mfe.llc',
  contractNumber: row.contractNumber || '',
  renewalDate: toDateTimeLocalValue(row.renewalDate),
  integrationFlags: { ...(row.integrationFlags || {}) },
  notes: row.notes || '',
}) : null;

const hydrateBillingSettingsDraft = settings => ({
  standardMonthly: centsToDollars(settings?.standardMonthlyCents ?? 24900),
  standardIncludedSeats: settings?.standardIncludedSeats ?? 25,
  standardAnnualPromoMonthly: centsToDollars(settings?.standardAnnualPromoMonthlyEquivalentCents ?? 24900),
  standardAnnualPromoMonths: settings?.standardAnnualPromoMonths ?? 12,
  standardAnnualPromoActive: settings?.standardAnnualPromoActive ?? true,
  premiumMonthly: centsToDollars(settings?.premiumMonthlyCents ?? 49900),
  premiumIncludedSeats: settings?.premiumIncludedSeats ?? 50,
  premiumIncludedAiReports: settings?.premiumIncludedAiReports ?? 50,
  premiumIncludedAiTokens: settings?.premiumIncludedAiTokens ?? 500000,
  premiumAnnualPromoMonthly: centsToDollars(settings?.premiumAnnualPromoMonthlyEquivalentCents ?? 24900),
  premiumAnnualPromoMonths: settings?.premiumAnnualPromoMonths ?? 12,
  premiumAnnualPromoActive: settings?.premiumAnnualPromoActive ?? true,
  seatPackSize: settings?.seatPackSize ?? 25,
  seatPackMonthly: centsToDollars(settings?.seatPackMonthlyCents ?? 9900),
  aiPackReportRuns: settings?.aiPackReportRuns ?? 50,
  aiPackTokens: settings?.aiPackTokens ?? 500000,
  aiPackMonthly: centsToDollars(settings?.aiPackMonthlyCents ?? 9900),
  smsDispatchPackMonthly: centsToDollars(settings?.smsDispatchPackMonthlyCents ?? 0),
  smsDispatchPackVisible: settings?.smsDispatchPackVisible ?? false,
  recoveryOpsPackMonthly: centsToDollars(settings?.recoveryOpsPackMonthlyCents ?? 0),
  recoveryOpsPackVisible: settings?.recoveryOpsPackVisible ?? false,
  standardStripeMonthlyPriceId: settings?.standardStripeMonthlyPriceId || '',
  standardStripeAnnualPromoPriceId: settings?.standardStripeAnnualPromoPriceId || '',
  premiumStripeMonthlyPriceId: settings?.premiumStripeMonthlyPriceId || '',
  premiumStripeAnnualPromoPriceId: settings?.premiumStripeAnnualPromoPriceId || '',
  seatPackStripeMonthlyPriceId: settings?.seatPackStripeMonthlyPriceId || '',
  aiPackStripeMonthlyPriceId: settings?.aiPackStripeMonthlyPriceId || '',
  smsDispatchPackStripeMonthlyPriceId: settings?.smsDispatchPackStripeMonthlyPriceId || '',
  recoveryOpsPackStripeMonthlyPriceId: settings?.recoveryOpsPackStripeMonthlyPriceId || '',
  stripePortalReturnUrl: settings?.stripePortalReturnUrl || '',
  stripeSuccessUrl: settings?.stripeSuccessUrl || '',
  stripeCancelUrl: settings?.stripeCancelUrl || '',
});

const emptyAnnouncementDraft = {
  title: '',
  body: '',
  severity: 'info',
  audienceScope: 'all_locations',
  location: '',
  role: '',
  isActive: true,
  startsAt: '',
  endsAt: '',
};

const emptyLocationDraft = {
  country: '',
  state: '',
  name: '',
  latitude: '',
  longitude: '',
};

const hydrateAnnouncementDraft = row => row ? ({
  title: row.title || '',
  body: row.body || '',
  severity: row.severity || 'info',
  audienceScope: row.audienceScope || 'all_locations',
  location: row.location || '',
  role: row.role || '',
  isActive: row.isActive !== false,
  startsAt: toDateTimeLocalValue(row.startsAt),
  endsAt: toDateTimeLocalValue(row.endsAt),
}) : { ...emptyAnnouncementDraft };

export default function MFEPlatformOwnerPortal({ api, user, selectedBase }) {
  const [rows, setRows] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(selectedBase || '');
  const [subscription, setSubscription] = useState(null);
  const [draft, setDraft] = useState(null);
  const [billingSettings, setBillingSettings] = useState(null);
  const [billingSettingsDraft, setBillingSettingsDraft] = useState(null);
  const [ownerMetrics, setOwnerMetrics] = useState(null);
  const [ownerMetricsLoading, setOwnerMetricsLoading] = useState(false);
  const [ownerLocationStatusFilter, setOwnerLocationStatusFilter] = useState('all');
  const [ownerLocationUserFilter, setOwnerLocationUserFilter] = useState('active');
  const [ownerLocationPlanFilter, setOwnerLocationPlanFilter] = useState('all');
  const [ownerLocationSort, setOwnerLocationSort] = useState('activity');
  const [announcements, setAnnouncements] = useState([]);
  const [superusers, setSuperusers] = useState([]);
  const [superuserSeedStatus, setSuperuserSeedStatus] = useState(null);
  const [superusersLoading, setSuperusersLoading] = useState(false);
  const [accountAccess, setAccountAccess] = useState(null);
  const [accountAccessLoading, setAccountAccessLoading] = useState(false);
  const [accountAccessSearch, setAccountAccessSearch] = useState('');
  const [accountAccessFilter, setAccountAccessFilter] = useState('all');
  const [accountAccessActionId, setAccountAccessActionId] = useState('');
  const [reassigningAccountId, setReassigningAccountId] = useState('');
  const [accountReassignmentDrafts, setAccountReassignmentDrafts] = useState({});
  const [blockDraft, setBlockDraft] = useState({ email: '', reason: '' });
  const [announcementDraft, setAnnouncementDraft] = useState(emptyAnnouncementDraft);
  const [locationDraft, setLocationDraft] = useState(emptyLocationDraft);
  const [editingAnnouncementId, setEditingAnnouncementId] = useState('');
  const [activeSection, setActiveSection] = useState('locations');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const detailRef = useRef(null);

  const isSuperuser = Boolean(user?.is_superuser);
  const integrationOptions = INTEGRATION_FLAG_OPTIONS.map(option => {
    const serverOption = (subscription?.availableIntegrationFlags || []).find(item => item.key === option.key);
    return serverOption || option;
  });

  const loadRows = async () => {
    if (!api || !user?.email || !isSuperuser) return;
    const res = await api.get('/admin/location-subscriptions', { params: { email: user.email } });
    const nextRows = res.data.data || [];
    setRows(nextRows);
    if (!selectedLocation && nextRows[0]?.location) setSelectedLocation(nextRows[0].location);
  };

  const loadBillingSettings = async () => {
    if (!api || !user?.email || !isSuperuser) return;
    const res = await api.get('/admin/billing-settings', { params: { email: user.email } });
    const settings = res.data.settings || {};
    setBillingSettings(settings);
    setBillingSettingsDraft(hydrateBillingSettingsDraft(settings));
  };

  const loadAnnouncements = async () => {
    if (!api || !user?.email || !isSuperuser) return;
    const res = await api.get('/admin/platform-announcements', { params: { email: user.email } });
    setAnnouncements(res.data.data || []);
  };

  const loadOwnerMetrics = async () => {
    if (!api || !user?.email || !isSuperuser) return null;
    try {
      setOwnerMetricsLoading(true);
      setError('');
      const res = await api.get('/admin/owner-metrics', {
        params: { email: user.email },
      });
      setOwnerMetrics(res.data || null);
      return res.data || null;
    } catch (err) {
      setOwnerMetrics(null);
      setError(err.response?.data?.message || 'Unable to load owner metrics.');
      return null;
    } finally {
      setOwnerMetricsLoading(false);
    }
  };

  const loadSuperusers = async () => {
    if (!api || !user?.email || !isSuperuser) return null;
    try {
      setSuperusersLoading(true);
      setError('');
      const res = await api.get('/admin/superusers', {
        params: { email: user.email },
      });
      setSuperusers(res.data.data || []);
      setSuperuserSeedStatus(res.data.seedStatus || null);
      return res.data || null;
    } catch (err) {
      setSuperusers([]);
      setSuperuserSeedStatus(null);
      setError(err.response?.data?.message || 'Unable to load platform superusers.');
      return null;
    } finally {
      setSuperusersLoading(false);
    }
  };

  const loadAccountAccess = async () => {
    if (!api || !user?.email || !isSuperuser) return null;
    try {
      setAccountAccessLoading(true);
      setError('');
      const res = await api.get('/admin/account-access', {
        params: { email: user.email },
      });
      setAccountAccess(res.data || null);
      return res.data || null;
    } catch (err) {
      setAccountAccess(null);
      setError(err.response?.data?.message || 'Unable to load account access controls.');
      return null;
    } finally {
      setAccountAccessLoading(false);
    }
  };

  const loadSubscription = async location => {
    if (!api || !user?.email || !location || !isSuperuser) return;
    const res = await api.get('/admin/location-subscription', {
      params: { email: user.email, location },
    });
    const nextSubscription = res.data.subscription || null;
    setSubscription(nextSubscription);
    setDraft(hydrateSubscriptionDraft(nextSubscription));
  };

  const loadPortal = async (location = selectedLocation || selectedBase) => {
    if (!isSuperuser) return;
    try {
      setIsLoading(true);
      setError('');
      await Promise.all([loadRows(), loadBillingSettings(), loadAnnouncements(), loadSuperusers()]);
      if (location) await loadSubscription(location);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load MFE Platform Owner Portal.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPortal(selectedLocation || selectedBase);
  }, [api, user?.email, user?.is_superuser]);

  useEffect(() => {
    if (activeSection === 'ownerMetrics') {
      loadOwnerMetrics();
    }
    if (activeSection === 'superusers') {
      loadSuperusers();
    }
    if (activeSection === 'accountAccess') {
      loadAccountAccess();
    }
  }, [activeSection, api, user?.email, user?.is_superuser]);

  const updateDraft = (field, value) => {
    setDraft(prev => ({ ...(prev || {}), [field]: value }));
    setMessage('');
    setError('');
  };

  const setOwnerAccountActive = async (row, nextActive) => {
    let reason = '';
    if (nextActive) {
      if (!window.confirm(`Reactivate ${row.email} and remove its registration block?`)) return;
    } else {
      reason = window.prompt(
        `Required reason for suspending ${row.email}:`,
        'Account access suspended by MFE owner'
      );
      if (reason === null) return;
      reason = reason.trim();
      if (!reason) {
        setError('A suspension reason is required.');
        return;
      }
      if (!window.confirm(`Suspend ${row.email} and block this email from registering again?`)) return;
    }

    try {
      setAccountAccessActionId(row.id);
      setMessage('');
      setError('');
      const res = await api.patch(`/admin/account-access/users/${row.id}`, {
        email: user.email,
        isActive: nextActive,
        reason,
      });
      setAccountAccess(res.data || null);
      setMessage(res.data.message || (nextActive ? 'Account reactivated.' : 'Account suspended.'));
      await loadOwnerMetrics();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to update account access.');
    } finally {
      setAccountAccessActionId('');
    }
  };

  const beginAccountReassignment = row => {
    setReassigningAccountId(row.id);
    setAccountReassignmentDrafts(current => ({
      ...current,
      [row.id]: {
        location: row.location || '',
        role: row.role || '',
        shop: row.shop || '',
        reason: '',
      },
    }));
    setMessage('');
    setError('');
  };

  const updateAccountReassignmentDraft = (userId, field, value) => {
    setAccountReassignmentDrafts(current => ({
      ...current,
      [userId]: {
        ...(current[userId] || {}),
        [field]: value,
      },
    }));
    setMessage('');
    setError('');
  };

  const saveAccountReassignment = async row => {
    const draft = accountReassignmentDrafts[row.id] || {};
    if (!draft.location || !draft.role || !draft.shop || !draft.reason?.trim()) {
      setError('Select a location, role, and shop, and enter a reassignment reason.');
      return;
    }
    if (!window.confirm(`Reassign ${row.email} to ${draft.location}?`)) return;

    try {
      setAccountAccessActionId(row.id);
      setMessage('');
      setError('');
      const res = await api.patch(`/admin/account-access/users/${row.id}/reassign`, {
        email: user.email,
        location: draft.location,
        role: draft.role,
        shop: draft.shop,
        reason: draft.reason.trim(),
      });
      setAccountAccess(res.data || null);
      setReassigningAccountId('');
      setAccountReassignmentDrafts(current => {
        const next = { ...current };
        delete next[row.id];
        return next;
      });
      setMessage(res.data.message || 'Account reassigned.');
      await loadOwnerMetrics();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to reassign the account.');
    } finally {
      setAccountAccessActionId('');
    }
  };

  const addRegistrationBlock = async event => {
    event.preventDefault();
    const blockedEmail = blockDraft.email.trim().toLowerCase();
    const reason = blockDraft.reason.trim();
    if (!blockedEmail || !reason) {
      setError('Enter an email and a required reason.');
      return;
    }
    if (!window.confirm(`Block ${blockedEmail} from registration and suspend it if already registered?`)) return;

    try {
      setAccountAccessActionId('new-block');
      setMessage('');
      setError('');
      const res = await api.post('/admin/account-access/blocks', {
        ownerEmail: user.email,
        blockedEmail,
        reason,
      });
      setAccountAccess(res.data || null);
      setBlockDraft({ email: '', reason: '' });
      setMessage(res.data.message || 'Registration block added.');
      await loadOwnerMetrics();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to add the registration block.');
    } finally {
      setAccountAccessActionId('');
    }
  };

  const removeRegistrationBlock = async block => {
    if (!window.confirm(`Remove the registration block for ${block.email}?`)) return;
    try {
      setAccountAccessActionId(block.id);
      setMessage('');
      setError('');
      const res = await api.patch(`/admin/account-access/blocks/${block.id}`, {
        email: user.email,
      });
      setAccountAccess(res.data || null);
      setMessage(res.data.message || 'Registration block removed.');
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to remove the registration block.');
    } finally {
      setAccountAccessActionId('');
    }
  };

  const updateIntegrationFlag = (key, value) => {
    setDraft(prev => ({
      ...(prev || {}),
      planName: inferPlanFromIntegrationFlags({
        ...((prev || {}).integrationFlags || {}),
        [key]: value,
      }),
      displayPlanName: inferPlanFromIntegrationFlags({
        ...((prev || {}).integrationFlags || {}),
        [key]: value,
      }),
      integrationFlags: {
        ...((prev || {}).integrationFlags || {}),
        [key]: value,
      },
    }));
    setMessage('');
    setError('');
  };

  const applyIntegrationBundle = bundle => {
    const nextFlags = { ...(draft?.integrationFlags || {}) };
    if (bundle === 'standard') {
      Object.assign(nextFlags, {
        commander_ai_brief: false,
        smart_custom_report_builder: false,
        launch_success_advisor: false,
        weather: false,
        status_exports: true,
        reports_pdf_export: true,
        notifications: true,
        personnel_sms_alerts: false,
        personnel_availability_prediction: false,
        safety_forms: true,
        lost_tool: true,
        recovery_personnel_scheduling: false,
        acft_plans: true,
        map_ramp_layout: true,
      });
      setDraft(prev => ({
        ...(prev || {}),
        planName: 'Standard',
        displayPlanName: 'Standard',
        integrationFlags: nextFlags,
        aiEnabled: false,
        aiMonthlyRequestLimit: 0,
        aiMonthlyTokenLimit: 0,
      }));
    } else if (bundle === 'premium') {
      Object.assign(nextFlags, {
        commander_ai_brief: true,
        smart_custom_report_builder: true,
        launch_success_advisor: true,
        weather: true,
        status_exports: true,
        reports_pdf_export: true,
        notifications: true,
        personnel_sms_alerts: false,
        personnel_availability_prediction: true,
        safety_forms: true,
        lost_tool: true,
        recovery_personnel_scheduling: true,
        acft_plans: true,
        map_ramp_layout: true,
      });
      setDraft(prev => ({
        ...(prev || {}),
        planName: 'Premium',
        displayPlanName: 'Premium',
        integrationFlags: nextFlags,
        aiEnabled: true,
        aiMonthlyRequestLimit: 50,
        aiMonthlyTokenLimit: 500000,
      }));
    }
    setMessage('');
    setError('');
  };

  const updateBillingSettingsDraft = (field, value) => {
    setBillingSettingsDraft(prev => ({ ...(prev || {}), [field]: value }));
    setMessage('');
    setError('');
  };

  const updateAnnouncementDraft = (field, value) => {
    setAnnouncementDraft(prev => ({ ...(prev || emptyAnnouncementDraft), [field]: value }));
    setMessage('');
    setError('');
  };

  const updateLocationDraft = (field, value) => {
    setLocationDraft(prev => ({ ...(prev || emptyLocationDraft), [field]: value }));
    setMessage('');
    setError('');
  };

  const openLocation = async location => {
    if (!location) return;
    try {
      setActiveSection('locations');
      setSelectedLocation(location);
      setSubscription(null);
      setDraft(null);
      setMessage('');
      setError('');
      setIsLoading(true);
      await loadSubscription(location);
      window.setTimeout(() => detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load that location.');
    } finally {
      setIsLoading(false);
    }
  };

  const saveSubscription = async () => {
    if (!draft || !selectedLocation) return;
    try {
      setIsSaving(true);
      setMessage('');
      setError('');
      const res = await api.patch('/admin/location-subscription', {
        email: user.email,
        location: selectedLocation,
        planName: draft.planName,
        subscriptionStatus: draft.subscriptionStatus,
        seatLimit: draft.seatLimit,
        seatAddOnQuantity: draft.seatAddOnQuantity,
        seatAddOnSeats: draft.seatAddOnSeats,
        aiAddOnQuantity: draft.aiAddOnQuantity,
        aiAddOnReportRuns: draft.aiAddOnReportRuns,
        aiAddOnTokens: draft.aiAddOnTokens,
        billingOwnerEmail: draft.billingOwnerEmail,
        billingContactEmail: draft.billingContactEmail,
        billingProvider: draft.billingProvider,
        billingPlanKey: draft.billingPlanKey,
        billingInterval: draft.billingInterval,
        providerCustomerId: draft.providerCustomerId,
        providerSubscriptionId: draft.providerSubscriptionId,
        pricePerMonthCents: dollarsToCents(draft.pricePerMonth),
        currency: draft.currency,
        trialEndsAt: fromDateTimeLocalValue(draft.trialEndsAt),
        currentPeriodEndsAt: fromDateTimeLocalValue(draft.currentPeriodEndsAt),
        aiEnabled: draft.aiEnabled,
        aiProvider: draft.aiProvider,
        aiKeyMode: draft.aiKeyMode,
        aiKeySecretRef: draft.aiKeySecretRef,
        aiMonthlyRequestLimit: draft.aiMonthlyRequestLimit,
        aiMonthlyTokenLimit: draft.aiMonthlyTokenLimit,
        customerAccountName: draft.customerAccountName,
        deploymentMode: draft.deploymentMode,
        deployedVersion: draft.deployedVersion,
        releaseChannel: draft.releaseChannel,
        supportContactName: draft.supportContactName,
        supportContactEmail: draft.supportContactEmail,
        contractNumber: draft.contractNumber,
        renewalDate: fromDateTimeLocalValue(draft.renewalDate),
        integrationFlags: draft.integrationFlags,
        notes: draft.notes,
      });
      const updated = res.data.subscription;
      setSubscription(updated);
      setDraft(hydrateSubscriptionDraft(updated));
      await loadRows();
      setMessage(res.data.message || 'Location billing and entitlements saved.');
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to save location billing settings.');
    } finally {
      setIsSaving(false);
    }
  };

  const updateSupportAccess = async nextEnabled => {
    if (!selectedLocation) return;
    const promptText = nextEnabled
      ? 'Optional reason for enabling MFE support access:'
      : 'Optional reason for revoking MFE support access:';
    const defaultReason = nextEnabled ? 'Owner support access enabled from MFE Owner Portal' : 'Owner support access revoked from MFE Owner Portal';
    const reason = window.prompt(promptText, defaultReason);
    if (reason === null) return;
    if (!nextEnabled && !window.confirm(`Revoke MFE support access for ${selectedLocation}?`)) return;

    try {
      setIsSaving(true);
      setMessage('');
      setError('');
      const res = await api.patch('/admin/location-support-access', {
        email: user.email,
        location: selectedLocation,
        enabled: nextEnabled,
        reason: reason || '',
      });
      const updated = res.data.subscription;
      setSubscription(updated);
      setDraft(hydrateSubscriptionDraft(updated));
      await loadRows();
      setMessage(res.data.message || 'MFE support access updated.');
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to update MFE support access.');
    } finally {
      setIsSaving(false);
    }
  };

  const saveBillingSettings = async () => {
    if (!billingSettingsDraft) return;
    try {
      setIsSaving(true);
      setMessage('');
      setError('');
      const res = await api.patch('/admin/billing-settings', {
        email: user.email,
        settings: {
          standardMonthlyCents: dollarsToCents(billingSettingsDraft.standardMonthly),
          standardIncludedSeats: billingSettingsDraft.standardIncludedSeats,
          standardAnnualPromoMonthlyEquivalentCents: dollarsToCents(billingSettingsDraft.standardAnnualPromoMonthly),
          standardAnnualPromoMonths: billingSettingsDraft.standardAnnualPromoMonths,
          standardAnnualPromoActive: billingSettingsDraft.standardAnnualPromoActive,
          premiumMonthlyCents: dollarsToCents(billingSettingsDraft.premiumMonthly),
          premiumIncludedSeats: billingSettingsDraft.premiumIncludedSeats,
          premiumIncludedAiReports: billingSettingsDraft.premiumIncludedAiReports,
          premiumIncludedAiTokens: billingSettingsDraft.premiumIncludedAiTokens,
          premiumAnnualPromoMonthlyEquivalentCents: dollarsToCents(billingSettingsDraft.premiumAnnualPromoMonthly),
          premiumAnnualPromoMonths: billingSettingsDraft.premiumAnnualPromoMonths,
          premiumAnnualPromoActive: billingSettingsDraft.premiumAnnualPromoActive,
          seatPackSize: billingSettingsDraft.seatPackSize,
          seatPackMonthlyCents: dollarsToCents(billingSettingsDraft.seatPackMonthly),
          aiPackReportRuns: billingSettingsDraft.aiPackReportRuns,
          aiPackTokens: billingSettingsDraft.aiPackTokens,
          aiPackMonthlyCents: dollarsToCents(billingSettingsDraft.aiPackMonthly),
          smsDispatchPackMonthlyCents: dollarsToCents(billingSettingsDraft.smsDispatchPackMonthly),
          smsDispatchPackVisible: billingSettingsDraft.smsDispatchPackVisible,
          recoveryOpsPackMonthlyCents: dollarsToCents(billingSettingsDraft.recoveryOpsPackMonthly),
          recoveryOpsPackVisible: billingSettingsDraft.recoveryOpsPackVisible,
          standardStripeMonthlyPriceId: billingSettingsDraft.standardStripeMonthlyPriceId,
          standardStripeAnnualPromoPriceId: billingSettingsDraft.standardStripeAnnualPromoPriceId,
          premiumStripeMonthlyPriceId: billingSettingsDraft.premiumStripeMonthlyPriceId,
          premiumStripeAnnualPromoPriceId: billingSettingsDraft.premiumStripeAnnualPromoPriceId,
          seatPackStripeMonthlyPriceId: billingSettingsDraft.seatPackStripeMonthlyPriceId,
          aiPackStripeMonthlyPriceId: billingSettingsDraft.aiPackStripeMonthlyPriceId,
          smsDispatchPackStripeMonthlyPriceId: billingSettingsDraft.smsDispatchPackStripeMonthlyPriceId,
          recoveryOpsPackStripeMonthlyPriceId: billingSettingsDraft.recoveryOpsPackStripeMonthlyPriceId,
          stripePortalReturnUrl: billingSettingsDraft.stripePortalReturnUrl,
          stripeSuccessUrl: billingSettingsDraft.stripeSuccessUrl,
          stripeCancelUrl: billingSettingsDraft.stripeCancelUrl,
        },
      });
      const settings = res.data.settings || {};
      setBillingSettings(settings);
      setBillingSettingsDraft(hydrateBillingSettingsDraft(settings));
      setMessage(res.data.message || 'Platform billing prices saved.');
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to save platform billing settings.');
    } finally {
      setIsSaving(false);
    }
  };

  const saveAnnouncement = async () => {
    try {
      setIsSaving(true);
      setMessage('');
      setError('');
      const payload = {
        email: user.email,
        ...announcementDraft,
        startsAt: fromDateTimeLocalValue(announcementDraft.startsAt),
        endsAt: fromDateTimeLocalValue(announcementDraft.endsAt),
      };
      const res = editingAnnouncementId
        ? await api.patch(`/admin/platform-announcements/${editingAnnouncementId}`, payload)
        : await api.post('/admin/platform-announcements', payload);
      await loadAnnouncements();
      setAnnouncementDraft({ ...emptyAnnouncementDraft });
      setEditingAnnouncementId('');
      setMessage(res.data.message || 'Platform announcement saved.');
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to save platform announcement.');
    } finally {
      setIsSaving(false);
    }
  };

  const addBaseLocation = async event => {
    event.preventDefault();
    if (!locationDraft.country.trim() || !locationDraft.state.trim() || !locationDraft.name.trim()) {
      setError('Country, region, and location name are required.');
      return;
    }

    try {
      setIsSaving(true);
      setMessage('');
      setError('');
      const res = await api.post('/admin/base-locations', {
        email: user.email,
        country: locationDraft.country,
        state: locationDraft.state,
        name: locationDraft.name,
        latitude: locationDraft.latitude,
        longitude: locationDraft.longitude,
      });
      const row = res.data.subscription;
      if (row) {
        setRows(prev => [row, ...prev.filter(item => item.location !== row.location)]);
        setSelectedLocation(row.location);
        setSubscription(row);
        setDraft(hydrateSubscriptionDraft(row));
      } else {
        await loadRows();
      }
      setLocationDraft({ ...emptyLocationDraft });
      setMessage(res.data.message || 'Base location added.');
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to add base location.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isSuperuser) {
    return (
      <div className="admin-tab">
        <section className="reports-panel admin-panel">
          <h3>Restricted</h3>
          <p className="reports-empty">This page is restricted.</p>
        </section>
      </div>
    );
  }

  const renderOwnerMetrics = () => {
    const allLocationRows = ownerMetrics?.locationActivityRows || ownerMetrics?.topLocations || [];
    const locationStatusOptions = Array.from(new Set(
      allLocationRows.map(row => row.subscriptionStatus || 'Unknown').filter(Boolean)
    )).sort((a, b) => a.localeCompare(b));
    const locationPlanOptions = Array.from(new Set(
      allLocationRows.map(row => row.displayPlanName || row.planName || 'Unknown').filter(Boolean)
    )).sort((a, b) => a.localeCompare(b));
    const filteredLocationRows = allLocationRows.filter(row => {
      const status = row.subscriptionStatus || 'Unknown';
      const plan = row.displayPlanName || row.planName || 'Unknown';
      const activeUserCount = Number(row.activeUserCount || 0);
      if (ownerLocationStatusFilter !== 'all' && status !== ownerLocationStatusFilter) return false;
      if (ownerLocationPlanFilter !== 'all' && plan !== ownerLocationPlanFilter) return false;
      if (ownerLocationUserFilter === 'active' && activeUserCount <= 0) return false;
      if (ownerLocationUserFilter === 'empty' && activeUserCount > 0) return false;
      return true;
    });
    const sortedLocationRows = [...filteredLocationRows].sort((a, b) => {
      const aiA = a.aiMonthlyUsage?.requestCount || 0;
      const aiB = b.aiMonthlyUsage?.requestCount || 0;
      const activeA = a.activeUserCount || 0;
      const activeB = b.activeUserCount || 0;
      const addOnsA = a.enabledAddOnCount || 0;
      const addOnsB = b.enabledAddOnCount || 0;
      const mrrA = a.pricePerMonthCents || 0;
      const mrrB = b.pricePerMonthCents || 0;
      const utilizationA = a.seatLimit ? activeA / a.seatLimit : 0;
      const utilizationB = b.seatLimit ? activeB / b.seatLimit : 0;
      if (ownerLocationSort === 'mrr') return (mrrB - mrrA) || (activeB - activeA) || (aiB - aiA);
      if (ownerLocationSort === 'ai') return (aiB - aiA) || (activeB - activeA) || (addOnsB - addOnsA);
      if (ownerLocationSort === 'addons') return (addOnsB - addOnsA) || (activeB - activeA) || (aiB - aiA);
      if (ownerLocationSort === 'seat_utilization') return (utilizationB - utilizationA) || (activeB - activeA) || (mrrB - mrrA);
      return (activeB - activeA) || (aiB - aiA) || (addOnsB - addOnsA) || (mrrB - mrrA);
    });
    const visibleLocationRows = sortedLocationRows.slice(0, 12);
    const filteredLabel = `${visibleLocationRows.length} of ${filteredLocationRows.length}`;

    return (
    <section className="reports-panel admin-panel admin-owner-metrics-panel">
      <div className="reports-panel__header">
        <div>
          <h3>MFE Owner Report</h3>
          <span>Business usage, billing readiness, subscription mix, and premium add-on signals across all locations.</span>
        </div>
        <button type="button" className="admin-refresh-button" onClick={loadOwnerMetrics} disabled={ownerMetricsLoading}>
          {ownerMetricsLoading ? 'Refreshing...' : 'Refresh Owner Report'}
        </button>
      </div>

      {!ownerMetrics ? (
        <p className="reports-empty">{ownerMetricsLoading ? 'Loading owner metrics...' : 'Owner metrics are not loaded yet.'}</p>
      ) : (
        <>
          <div className="admin-billing-note">
            This report is for MFE product-owner decisions: active sites, subscription health, estimated MRR, feature adoption, AI usage, and upsell opportunities. Revenue is estimated from the location subscription records in this app.
          </div>

          <div className="admin-owner-kpi-grid">
            <div><span>Live Sites</span><strong>{ownerMetrics.kpis?.liveSites || 0}</strong><small>{ownerMetrics.kpis?.trialSites || 0} trial / {ownerMetrics.kpis?.activeSites || 0} active</small></div>
            <div>
              <span>Active Customer Accounts</span>
              <strong>{ownerMetrics.kpis?.activeCustomerUsers || 0}</strong>
              <small>
                {ownerMetrics.kpis?.mappedActiveCustomerUsers || 0} mapped
                {(ownerMetrics.kpis?.unmappedActiveCustomerUsers || 0) > 0
                  ? ` / ${ownerMetrics.kpis.unmappedActiveCustomerUsers} needs location review`
                  : ` / ${ownerMetrics.kpis?.recentLoginUsers30d || 0} logged in last 30 days`}
              </small>
            </div>
            <div><span>Billable MRR</span><strong>{formatCurrencyFromCents(ownerMetrics.kpis?.billableMrrCents)}</strong><small>{formatCurrencyFromCents(ownerMetrics.kpis?.pipelineMrrCents)} incl. trials/past due</small></div>
            <div><span>Premium Sites</span><strong>{ownerMetrics.kpis?.premiumSites || 0}</strong><small>AI or premium add-ons enabled</small></div>
            <div><span>Seat Utilization</span><strong>{ownerMetrics.kpis?.seatUtilizationPercent || 0}%</strong><small>{ownerMetrics.kpis?.mappedActiveCustomerUsers || 0}/{ownerMetrics.kpis?.seatLimitTotal || 0} mapped seats</small></div>
            <div><span>AI Requests</span><strong>{ownerMetrics.kpis?.aiRequestsThisMonth || 0}</strong><small>{(ownerMetrics.kpis?.aiTokensThisMonth || 0).toLocaleString()} tokens this month</small></div>
          </div>

          <div className="admin-owner-report-grid">
            <div className="admin-billing-subpanel">
              <h4>Plan Mix</h4>
              <div className="admin-owner-count-list">
                {(ownerMetrics.planMix || []).map(row => <div key={row.label}><span>{row.label}</span><strong>{row.value}</strong></div>)}
              </div>
            </div>
            <div className="admin-billing-subpanel">
              <h4>Subscription Status</h4>
              <div className="admin-owner-count-list">
                {(ownerMetrics.statusMix || []).map(row => <div key={row.label}><span>{row.label}</span><strong>{row.value}</strong></div>)}
              </div>
            </div>
            <div className="admin-billing-subpanel">
              <h4>Deployment Mode</h4>
              <div className="admin-owner-count-list">
                {(ownerMetrics.deploymentMix || []).map(row => <div key={row.label}><span>{formatDeploymentMode(row.label)}</span><strong>{row.value}</strong></div>)}
              </div>
            </div>
          </div>

          <div className="admin-billing-subpanel">
            <div><h4>Premium / Add-On Adoption</h4><p>Use this to see what features are being enabled and where packaging or pricing may need attention.</p></div>
            <div className="admin-owner-addon-grid">
              {(ownerMetrics.addOnAdoption || []).map(row => (
                <div key={row.key}><span>{row.label}</span><strong>{row.enabledLocations}</strong><small>{row.adoptionPercent}% of locations</small></div>
              ))}
            </div>
          </div>

          <div className="admin-owner-report-grid">
            <div className="admin-billing-subpanel">
              <h4>Heavily Used Areas</h4>
              <div className="admin-owner-usage-list">
                {(ownerMetrics.usage || []).map(row => (
                  <div key={row.label}><span>{row.label}</span><strong>{row.value}</strong><small>{row.total} all-time</small></div>
                ))}
              </div>
            </div>
            <div className="admin-billing-subpanel">
              <h4>AI Usage By Feature</h4>
              <div className="admin-owner-count-list">
                {(ownerMetrics.aiUsageByFeature || []).length > 0
                  ? ownerMetrics.aiUsageByFeature.map(row => <div key={row.label}><span>{row.label}</span><strong>{row.value}</strong></div>)
                  : <p className="reports-empty">No AI usage this month.</p>}
              </div>
            </div>
          </div>

          <div className="admin-billing-subpanel">
            <div><h4>Upgrade / Billing Opportunities</h4><p>Signals that may justify more seats, AI, SMS, recovery scheduling, or premium bundles.</p></div>
            <div className="admin-owner-opportunity-list">
              {(ownerMetrics.opportunities || []).length > 0 ? ownerMetrics.opportunities.map((item, index) => (
                <div key={`${item.location}-${item.type}-${index}`}><strong>{item.type}</strong><span>{item.location}</span><small>{item.details}</small></div>
              )) : <p className="reports-empty">No current opportunity signals.</p>}
            </div>
          </div>

          <div className="admin-billing-subpanel admin-owner-location-activity-panel">
            <div className="reports-panel__header admin-owner-location-activity-header">
              <div>
                <h4>Top Location Activity</h4>
                <p>Showing up to 12 ranked locations from the active base list.</p>
              </div>
              <span className="admin-status-pill is-active">{filteredLabel} shown</span>
            </div>
            <div className="admin-owner-location-filters">
              <label>
                Status
                <select value={ownerLocationStatusFilter} onChange={event => setOwnerLocationStatusFilter(event.target.value)}>
                  <option value="all">All statuses</option>
                  {locationStatusOptions.map(status => <option key={status} value={status}>{status}</option>)}
                </select>
              </label>
              <label>
                Active Users
                <select value={ownerLocationUserFilter} onChange={event => setOwnerLocationUserFilter(event.target.value)}>
                  <option value="active">Has active users</option>
                  <option value="all">All locations</option>
                  <option value="empty">No active users</option>
                </select>
              </label>
              <label>
                Plan / Bundle
                <select value={ownerLocationPlanFilter} onChange={event => setOwnerLocationPlanFilter(event.target.value)}>
                  <option value="all">All plans</option>
                  {locationPlanOptions.map(plan => <option key={plan} value={plan}>{plan}</option>)}
                </select>
              </label>
              <label>
                Rank By
                <select value={ownerLocationSort} onChange={event => setOwnerLocationSort(event.target.value)}>
                  <option value="activity">Active users</option>
                  <option value="mrr">Monthly revenue</option>
                  <option value="ai">AI usage</option>
                  <option value="addons">Add-ons</option>
                  <option value="seat_utilization">Seat utilization</option>
                </select>
              </label>
            </div>
            <div className="reports-table-shell">
              <table className="reports-table admin-owner-metrics-table">
                <thead>
                  <tr>
                    <th>Location</th>
                    <th>Status</th>
                    <th>Plan</th>
                    <th>Active Users</th>
                    <th>Seat Limit</th>
                    <th>Monthly Price</th>
                    <th>AI</th>
                    <th>AI Usage</th>
                    <th>Add-Ons</th>
                    <th>Renewal / Period End</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleLocationRows.length > 0 ? visibleLocationRows.map(row => (
                    <tr key={row.location}>
                      <td><strong>{row.location}</strong><span>{row.state || ''} {row.country || ''}</span></td>
                      <td><span className={`admin-status-pill is-${row.subscriptionStatus || 'inactive'}`}>{row.subscriptionStatus || '--'}</span></td>
                      <td>{row.displayPlanName || row.planName || '--'}</td>
                      <td>{row.activeUserCount || 0}</td>
                      <td>{row.seatLimit || 0}</td>
                      <td>{row.pricePerMonthCents ? formatCurrencyFromCents(row.pricePerMonthCents) : '--'}</td>
                      <td>{row.aiEnabled ? row.runtimeAiProvider || row.aiProvider || 'Enabled' : 'Off'}</td>
                      <td>{row.aiMonthlyUsage?.requestCount ?? 0}{row.aiMonthlyRequestLimit ? ` / ${row.aiMonthlyRequestLimit}` : ' / unlimited'}</td>
                      <td>{row.enabledAddOnCount || 0}</td>
                      <td>{formatLocalDateTime(row.currentPeriodEndsAt || row.trialEndsAt) || '--'}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={10} className="reports-empty">No locations match these filters.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </section>
    );
  };

  const renderAccountAccess = () => {
    const accounts = accountAccess?.accounts || [];
    const summary = accountAccess?.summary || {};
    const activeLocationOptions = accountAccess?.activeLocationOptions || [];
    const roleOptions = accountAccess?.roleOptions || [];
    const shopOptions = accountAccess?.shopOptions || [];
    const normalizedSearch = accountAccessSearch.trim().toLowerCase();
    const filteredAccounts = accounts.filter(row => {
      const matchesSearch = !normalizedSearch || [
        row.email,
        row.location,
        row.role,
        row.shop,
        row.userType,
        row.accountCategory,
      ].some(value => String(value || '').toLowerCase().includes(normalizedSearch));
      if (!matchesSearch) return false;
      if (accountAccessFilter === 'active') return row.isActive && !row.isInternal;
      if (accountAccessFilter === 'suspended') return !row.isActive && !row.isInternal;
      if (accountAccessFilter === 'blocked') return row.registrationBlocked;
      if (accountAccessFilter === 'unmapped') return row.locationStatus !== 'active' && !row.isInternal;
      if (accountAccessFilter === 'internal') return row.isInternal;
      return true;
    });
    const standaloneBlocks = accountAccess?.standaloneBlocks || [];

    return (
      <section className="reports-panel admin-panel admin-billing-panel admin-owner-account-access-panel">
        <div className="reports-panel__header">
          <div>
            <h3>Account Access Controls</h3>
            <span>Owner-only account inventory, suspension controls, and registration blocks.</span>
          </div>
          <button type="button" className="admin-refresh-button" onClick={loadAccountAccess} disabled={accountAccessLoading}>
            {accountAccessLoading ? 'Refreshing...' : 'Refresh Accounts'}
          </button>
        </div>

        <div className="admin-billing-note">
          This view intentionally shows only account-administration data. It does not expose passwords, CAC identity values, location history, or customer operational records.
        </div>

        <div className="admin-owner-superuser-seed-grid admin-owner-account-summary">
          <div><span>Registered</span><strong>{summary.registeredAccounts || 0}</strong><small>Customer and protected internal accounts</small></div>
          <div><span>Active Customers</span><strong>{summary.activeCustomerAccounts || 0}</strong><small>{summary.mappedActiveCustomerAccounts || 0} mapped to active locations</small></div>
          <div><span>Needs Location Review</span><strong>{summary.unmappedActiveCustomerAccounts || 0}</strong><small>Inactive, renamed, or unmatched locations</small></div>
          <div><span>Suspended</span><strong>{summary.suspendedAccounts || 0}</strong><small>Customer login access disabled</small></div>
          <div><span>Registration Blocks</span><strong>{summary.registrationBlocks || 0}</strong><small>Verification email and registration denied</small></div>
        </div>

        <form className="admin-owner-block-form" onSubmit={addRegistrationBlock}>
          <div>
            <strong>Block an email</strong>
            <span>Use for a registered account or to prevent a new registration.</span>
          </div>
          <label>
            Email
            <input
              type="email"
              value={blockDraft.email}
              onChange={event => setBlockDraft(current => ({ ...current, email: event.target.value }))}
              placeholder="user@example.mil"
              required
            />
          </label>
          <label>
            Required reason
            <input
              value={blockDraft.reason}
              onChange={event => setBlockDraft(current => ({ ...current, reason: event.target.value }))}
              placeholder="Reason for access restriction"
              maxLength={1000}
              required
            />
          </label>
          <button type="submit" className="admin-owner-action is-danger" disabled={accountAccessActionId === 'new-block'}>
            {accountAccessActionId === 'new-block' ? 'Blocking...' : 'Block Access'}
          </button>
        </form>

        <div className="admin-owner-account-filters">
          <input
            type="search"
            value={accountAccessSearch}
            onChange={event => setAccountAccessSearch(event.target.value)}
            placeholder="Search email, location, role, or account type"
          />
          <select value={accountAccessFilter} onChange={event => setAccountAccessFilter(event.target.value)}>
            <option value="all">All registered accounts</option>
            <option value="active">Active customer accounts</option>
            <option value="suspended">Suspended customer accounts</option>
            <option value="blocked">Registration blocked</option>
            <option value="unmapped">Needs location review</option>
            <option value="internal">Protected MFE accounts</option>
          </select>
          <span>{filteredAccounts.length} shown</span>
        </div>

        <div className="reports-table-shell">
          <table className="reports-table admin-owner-account-access-table">
            <thead>
              <tr>
                <th>Account</th>
                <th>Category</th>
                <th>Location</th>
                <th>Role / Shop</th>
                <th>Status</th>
                <th>Last Login / CAC</th>
                <th>Access Record</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAccounts.length > 0 ? filteredAccounts.map(row => (
                <tr key={row.id || row.email} className={!row.isActive ? 'is-inactive-user' : ''}>
                  <td>
                    <strong>{row.email}</strong>
                    <span>{row.userType || 'No user type'}</span>
                  </td>
                  <td>
                    <span className={`admin-status-pill ${row.isInternal ? 'is-inactive' : 'is-active'}`}>
                      {row.accountCategory}
                    </span>
                  </td>
                  <td>
                    <strong>{row.location || '--'}</strong>
                    <span className={`admin-account-location-status is-${row.locationStatus || 'unmapped'}`}>
                      {row.locationStatus === 'active' ? 'Mapped active location' : row.locationStatus === 'inactive' ? 'Inactive location' : 'Unmapped location'}
                    </span>
                  </td>
                  <td>
                    <strong>{row.role || '--'}</strong>
                    <span>{row.shop || 'No shop assigned'}</span>
                  </td>
                  <td>
                    <span className={`admin-status-pill ${row.isActive ? 'is-active' : 'is-suspended'}`}>
                      {row.isActive ? 'Active' : 'Suspended'}
                    </span>
                    {row.registrationBlocked ? <span className="admin-link-pill is-conflict">Registration blocked</span> : null}
                  </td>
                  <td>
                    <strong>{formatLocalDateTime(row.lastLoginAt) || 'No login recorded'}</strong>
                    <span>{row.loggedInLast30Days ? 'Logged in within 30 days' : 'No login within 30 days'}</span>
                    <span>{row.cacLinked ? 'CAC / PIV linked' : 'CAC / PIV not linked'}</span>
                  </td>
                  <td>
                    {row.registrationBlocked ? (
                      <>
                        <strong>{row.block?.reason || row.deactivationReason || 'Blocked'}</strong>
                        <span>{formatLocalDateTime(row.block?.createdAt || row.deactivatedAt) || ''}</span>
                        <span>{row.block?.createdBy || row.deactivatedBy || ''}</span>
                      </>
                    ) : row.deactivatedAt ? (
                      <>
                        <strong>{row.deactivationReason || 'Suspended'}</strong>
                        <span>{formatLocalDateTime(row.deactivatedAt)}</span>
                        <span>{row.deactivatedBy || ''}</span>
                      </>
                    ) : '--'}
                  </td>
                  <td>
                    {row.isInternal ? (
                      <span className="admin-link-pill is-unlinked">Protected here</span>
                    ) : (
                      <div className="admin-owner-account-actions">
                        {row.userType === 'USAF' || row.userType === 'USArmy' ? (
                          reassigningAccountId === row.id ? (
                            <div className="admin-owner-reassign-form">
                              <select
                                aria-label={`New location for ${row.email}`}
                                value={accountReassignmentDrafts[row.id]?.location || ''}
                                onChange={event => updateAccountReassignmentDraft(row.id, 'location', event.target.value)}
                              >
                                <option value="">Select location</option>
                                {activeLocationOptions.map(location => (
                                  <option key={location} value={location}>{location}</option>
                                ))}
                              </select>
                              <select
                                aria-label={`New role for ${row.email}`}
                                value={accountReassignmentDrafts[row.id]?.role || ''}
                                onChange={event => updateAccountReassignmentDraft(row.id, 'role', event.target.value)}
                              >
                                <option value="">Select role</option>
                                {roleOptions.map(role => <option key={role} value={role}>{role}</option>)}
                              </select>
                              <select
                                aria-label={`New shop for ${row.email}`}
                                value={accountReassignmentDrafts[row.id]?.shop || ''}
                                onChange={event => updateAccountReassignmentDraft(row.id, 'shop', event.target.value)}
                              >
                                <option value="">Select shop</option>
                                {shopOptions.map(shop => <option key={shop} value={shop}>{shop}</option>)}
                              </select>
                              <input
                                type="text"
                                maxLength={500}
                                placeholder="Required reassignment reason"
                                value={accountReassignmentDrafts[row.id]?.reason || ''}
                                onChange={event => updateAccountReassignmentDraft(row.id, 'reason', event.target.value)}
                              />
                              <div className="admin-owner-reassign-buttons">
                                <button
                                  type="button"
                                  className="admin-owner-action"
                                  onClick={() => saveAccountReassignment(row)}
                                  disabled={accountAccessActionId === row.id}
                                >
                                  {accountAccessActionId === row.id ? 'Saving...' : 'Save Reassignment'}
                                </button>
                                <button
                                  type="button"
                                  className="admin-owner-action"
                                  onClick={() => setReassigningAccountId('')}
                                  disabled={accountAccessActionId === row.id}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="admin-owner-action"
                              onClick={() => beginAccountReassignment(row)}
                              disabled={accountAccessActionId === row.id}
                            >
                              Reassign
                            </button>
                          )
                        ) : null}
                        {row.isActive ? (
                          <button
                            type="button"
                            className="admin-owner-action is-danger"
                            onClick={() => setOwnerAccountActive(row, false)}
                            disabled={accountAccessActionId === row.id}
                          >
                            {accountAccessActionId === row.id ? 'Working...' : 'Suspend + Block'}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="admin-owner-action is-restore"
                            onClick={() => setOwnerAccountActive(row, true)}
                            disabled={accountAccessActionId === row.id}
                          >
                            {accountAccessActionId === row.id ? 'Working...' : 'Reactivate'}
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={8} className="reports-empty">
                    {accountAccessLoading ? 'Loading registered accounts...' : 'No accounts match the selected filters.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {standaloneBlocks.length > 0 ? (
          <div className="admin-owner-standalone-blocks">
            <h4>Blocked Emails Without Registered Accounts</h4>
            {standaloneBlocks.map(block => (
              <div key={block.id}>
                <span><strong>{block.email}</strong>{block.reason}</span>
                <span>{formatLocalDateTime(block.createdAt)} by {block.createdBy || '--'}</span>
                <button
                  type="button"
                  className="admin-owner-action is-restore"
                  onClick={() => removeRegistrationBlock(block)}
                  disabled={accountAccessActionId === block.id}
                >
                  Remove Block
                </button>
              </div>
            ))}
          </div>
        ) : null}
      </section>
    );
  };

  const renderSuperusers = () => {
    const activeCount = superusers.filter(row => row.isActive).length;
    const startupSeedEmails = superuserSeedStatus?.seedTargetEmails || [];
    const allowedOwnerEmails = superuserSeedStatus?.allowedOwnerEmails || [];
    return (
      <section className="reports-panel admin-panel admin-billing-panel admin-owner-superusers-panel">
        <div className="reports-panel__header">
          <div>
            <h3>Platform Superusers</h3>
            <span>Read-only view of users currently marked as superusers in the database.</span>
          </div>
          <button type="button" className="admin-refresh-button" onClick={loadSuperusers} disabled={superusersLoading}>
            {superusersLoading ? 'Refreshing...' : 'Refresh Superusers'}
          </button>
        </div>

        <div className="admin-billing-note">
          Current access comes from the users table. Startup seed settings keep the bootstrap owner account available on backend restart. Allowed owner emails are upgraded only after the user completes normal registration and links CAC / PIV.
        </div>

        <div className="admin-owner-superuser-seed-grid">
          <div>
            <span>DB Superusers</span>
            <strong>{superusers.length}</strong>
            <small>{activeCount} active</small>
          </div>
          <div>
            <span>Startup Seed</span>
            <strong>{superuserSeedStatus?.hasStartupPassword ? 'Enabled' : 'Password Missing'}</strong>
            <small>Only emails marked as seed targets can be reasserted.</small>
          </div>
          <div>
            <span>Seed Targets</span>
            <strong>{startupSeedEmails.length || 0}</strong>
            <small>{startupSeedEmails.join(', ') || 'None configured'}</small>
          </div>
          <div>
            <span>Allowed Owner Emails</span>
            <strong>{allowedOwnerEmails.length || 0}</strong>
            <small>{allowedOwnerEmails.join(', ') || 'None configured'}; CAC / PIV link required for upgrade.</small>
          </div>
        </div>

        <div className="reports-table-shell">
          <table className="reports-table admin-owner-superusers-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Status</th>
                <th>Location</th>
                <th>Role / Shop</th>
                <th>User Type</th>
                <th>Login / CAC</th>
                <th>Startup Seed</th>
              </tr>
            </thead>
            <tbody>
              {superusers.length > 0 ? superusers.map(row => (
                <tr key={row.id || row.email}>
                  <td>
                    <strong>{row.email}</strong>
                    {row.isCurrentRequester ? <span>You are signed in with this account.</span> : null}
                  </td>
                  <td>
                    <span className={`admin-status-pill ${row.isActive ? 'is-active' : 'is-inactive'}`}>
                      {row.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{row.location || '--'}</td>
                  <td>
                    <strong>{row.role || '--'}</strong>
                    <span>{row.shop || 'No shop assigned'}</span>
                  </td>
                  <td>{row.userType || '--'}</td>
                  <td>
                    <strong>{formatLocalDateTime(row.lastLoginAt) || 'No login recorded'}</strong>
                    <span>{row.cacLinked ? `CAC linked ${formatLocalDateTime(row.cacLinkedAt) || ''}` : 'CAC not linked'}</span>
                  </td>
                  <td>
                    {row.willBeReassertedOnRestart ? (
                      <span className="admin-status-pill is-active">Reasserts</span>
                    ) : row.isBootstrapSeedTarget ? (
                      <span className="admin-status-pill is-trial">Seed Target</span>
                    ) : row.isBootstrapAllowedEmail ? (
                      <span className="admin-status-pill is-inactive">CAC Upgrade Allowed</span>
                    ) : (
                      <span className="admin-status-pill is-inactive">DB Only</span>
                    )}
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={7} className="reports-empty">
                    {superusersLoading ? 'Loading platform superusers...' : 'No superuser rows found.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    );
  };

  const renderLocationConsole = () => (
    <>
      <section className="reports-panel admin-panel admin-owner-location-create">
        <div className="reports-panel__header">
          <div>
            <h3>Add Base Location</h3>
            <span>Add countries, regions, and bases to the registration and transfer dropdowns without editing code.</span>
          </div>
        </div>
        <form className="admin-billing-grid" onSubmit={addBaseLocation}>
          <label>Country / Area<input value={locationDraft.country} onChange={event => updateLocationDraft('country', event.target.value)} placeholder="Japan" /></label>
          <label>Region<input value={locationDraft.state} onChange={event => updateLocationDraft('state', event.target.value)} placeholder="Okinawa" /></label>
          <label>Location Name<input value={locationDraft.name} onChange={event => updateLocationDraft('name', event.target.value)} placeholder="Kadena Air Base" /></label>
          <label>Latitude<input type="number" step="0.0001" value={locationDraft.latitude} onChange={event => updateLocationDraft('latitude', event.target.value)} placeholder="Optional" /></label>
          <label>Longitude<input type="number" step="0.0001" value={locationDraft.longitude} onChange={event => updateLocationDraft('longitude', event.target.value)} placeholder="Optional" /></label>
          <div className="admin-billing-actions">
            <button type="submit" disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Add Location'}
            </button>
          </div>
        </form>
      </section>

      <section className="reports-panel admin-panel admin-owner-billing-panel">
        <div className="reports-panel__header">
          <div>
            <h3>Location Billing Console</h3>
            <span>Superuser view of every billable military location.</span>
          </div>
        </div>
        <div className="reports-table-shell">
          <table className="reports-table admin-owner-billing-table">
            <thead>
              <tr>
                <th>Location</th>
                <th>Active Users / Seats</th>
                <th>A La Carte</th>
                <th>Plan</th>
                <th>Status</th>
                <th>Billing Owner</th>
                <th>Billing Contact</th>
                <th>Support Contact</th>
                <th>MFE Support</th>
                <th>Display Monthly Price</th>
                <th>AI</th>
                <th>AI Usage</th>
                <th>Add-Ons</th>
                <th>Deployment</th>
                <th>Version</th>
                <th>Trial / Period End</th>
                <th>Renewal</th>
                <th>Last Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length > 0 ? rows.map(row => (
                <tr
                  key={row.location}
                  className={row.location === selectedLocation ? 'is-selected-owner-row' : ''}
                  onClick={() => openLocation(row.location)}
                >
                  <td>
                    <strong>{row.location}</strong>
                    <span>{[row.state, row.country].filter(Boolean).join(', ')}</span>
                  </td>
                  <td>{row.activeUserCount || 0} / {row.seatLimit || 0}</td>
                  <td>
                    <strong>{row.seatAddOnQuantity || 0} packs</strong>
                    <span>{row.seatAddOnSeats || 0} paid add-on seats</span>
                    <span>{row.aiAddOnQuantity || 0} AI packs</span>
                  </td>
                  <td>{row.displayPlanName || row.planName || '--'}</td>
                  <td><span className={`admin-status-pill is-${row.subscriptionStatus || 'inactive'}`}>{row.subscriptionStatus || '--'}</span></td>
                  <td>{row.billingOwnerEmail || '--'}</td>
                  <td>{row.billingContactEmail || '--'}</td>
                  <td>{row.supportContactEmail || row.supportContactName || '--'}</td>
                  <td>
                    <span className={`admin-status-pill ${row.mfeSupportAccessEnabled ? 'is-active' : 'is-inactive'}`}>
                      {row.mfeSupportAccessEnabled ? 'Allowed' : 'Off'}
                    </span>
                  </td>
                  <td>{row.pricePerMonthCents ? `$${centsToDollars(row.pricePerMonthCents)}` : '--'}</td>
                  <td>{row.aiEnabled ? 'Enabled' : 'Off'}</td>
                  <td className="admin-ai-usage-cell">
                    <strong>{row.aiMonthlyUsage?.requestCount ?? 0}{row.aiMonthlyRequestLimit ? ` / ${row.aiMonthlyRequestLimit}` : ' / unlimited'} req</strong>
                    <span>{(row.aiMonthlyUsage?.totalTokens || 0).toLocaleString()}{row.aiMonthlyTokenLimit ? ` / ${Number(row.aiMonthlyTokenLimit).toLocaleString()}` : ' / unlimited'} tokens</span>
                  </td>
                  <td>{row.enabledAddOnCount || 0} enabled</td>
                  <td>{formatDeploymentMode(row.deploymentMode)}</td>
                  <td>{row.deployedVersion || row.releaseChannel || '--'}</td>
                  <td>{formatLocalDateTime(row.trialOrPeriodEnd) || '--'}</td>
                  <td>{formatLocalDateTime(row.renewalDate) || '--'}</td>
                  <td>{formatLocalDateTime(row.updatedAt) || '--'}</td>
                  <td>
                    <button
                      type="button"
                      className="admin-owner-action"
                      onClick={event => {
                        event.stopPropagation();
                        openLocation(row.location);
                      }}
                    >
                      Open / Edit
                    </button>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={19} className="reports-empty">
                    {isLoading ? 'Loading location billing console...' : 'No active locations found.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section ref={detailRef} className="reports-panel admin-panel admin-billing-panel admin-billing-panel--selected">
        <div className="reports-panel__header">
          <div>
            <h3>Location Billing Detail</h3>
            <span>{selectedLocation || 'Select a location'} subscription, contracts, deployment, AI access, and add-on integration flags.</span>
          </div>
          <span className={`admin-status-pill is-${subscription?.subscriptionStatus || 'inactive'}`}>
            {subscription?.subscriptionStatus || 'Not loaded'}
          </span>
        </div>

        {!draft ? (
          <p className="reports-empty">{isLoading ? 'Loading billing settings...' : 'Select a location to manage billing settings.'}</p>
        ) : (
          <>
            <div className="admin-billing-summary">
              <div><span>Active Users</span><strong>{subscription.activeUserCount || 0}</strong></div>
              <div><span>Seat Limit</span><strong>{draft.seatLimit || 0}</strong></div>
              <div><span>Purchased Seat Packs</span><strong>{draft.seatAddOnQuantity || 0}</strong><small>{draft.seatAddOnSeats || 0} paid add-on seats</small></div>
              <div><span>Purchased AI Packs</span><strong>{draft.aiAddOnQuantity || 0}</strong><small>{Number(draft.aiAddOnReportRuns || 0).toLocaleString()} report runs / {Number(draft.aiAddOnTokens || 0).toLocaleString()} tokens</small></div>
              <div><span>Plan</span><strong>{draft.displayPlanName || draft.planName}</strong></div>
              <div><span>Display Monthly Price</span><strong>{draft.pricePerMonth ? `$${draft.pricePerMonth}` : '--'}</strong></div>
              <div>
                <span>AI Usage This Month</span>
                <strong>{subscription.aiMonthlyUsage?.requestCount ?? 0}{draft.aiMonthlyRequestLimit ? ` / ${draft.aiMonthlyRequestLimit}` : ' / unlimited'}</strong>
                <small>{(subscription.aiMonthlyUsage?.totalTokens || 0).toLocaleString()} tokens{draft.aiMonthlyTokenLimit ? ` / ${Number(draft.aiMonthlyTokenLimit).toLocaleString()}` : ' / unlimited'}</small>
              </div>
            </div>

            <div className="admin-billing-subpanel admin-support-access-panel">
              <div>
                <h4>MFE Support Access</h4>
                <p>Customer-granted support access for the owner support account ({draft.mfeOwnerSupportEmail || 'info@mfe.llc'}). Current superuser access remains enabled for internal testing, but this records the future customer-controlled support workflow.</p>
              </div>
              <div className="admin-support-access-status">
                <span className={`admin-status-pill ${draft.mfeSupportAccessEnabled ? 'is-active' : 'is-inactive'}`}>
                  {draft.mfeSupportAccessEnabled ? 'Allowed' : 'Off'}
                </span>
                <span>
                  {draft.mfeSupportAccessEnabled
                    ? `Granted by ${draft.mfeSupportAccessGrantedBy || '--'} ${formatLocalDateTime(draft.mfeSupportAccessGrantedAt) || ''}`
                    : 'No active customer support grant recorded.'}
                </span>
                {draft.mfeSupportAccessReason ? <small>{draft.mfeSupportAccessReason}</small> : null}
                <button
                  type="button"
                  className={draft.mfeSupportAccessEnabled ? 'profile-modal__secondary' : ''}
                  onClick={() => updateSupportAccess(!draft.mfeSupportAccessEnabled)}
                  disabled={isSaving}
                >
                  {draft.mfeSupportAccessEnabled ? 'Revoke Support Access' : 'Allow Support Access'}
                </button>
              </div>
            </div>

            <div className="admin-billing-grid">
              <label>Customer Account<input value={draft.customerAccountName} onChange={event => updateDraft('customerAccountName', event.target.value)} /></label>
              <label>Billing Owner<input type="email" value={draft.billingOwnerEmail} onChange={event => updateDraft('billingOwnerEmail', event.target.value)} /></label>
              <label>Billing Contact<input type="email" value={draft.billingContactEmail} onChange={event => updateDraft('billingContactEmail', event.target.value)} /></label>
              <label>Support Contact<input value={draft.supportContactName} onChange={event => updateDraft('supportContactName', event.target.value)} /></label>
              <label>Support Email<input type="email" value={draft.supportContactEmail} onChange={event => updateDraft('supportContactEmail', event.target.value)} /></label>
              <label>Contract Number<input value={draft.contractNumber} onChange={event => updateDraft('contractNumber', event.target.value)} /></label>
              <label>Renewal Date<input type="datetime-local" value={draft.renewalDate} onChange={event => updateDraft('renewalDate', event.target.value)} /></label>
              <label>Deployment Mode<select value={draft.deploymentMode} onChange={event => updateDraft('deploymentMode', event.target.value)}><option value="mfe_hosted_saas">MFE-hosted SaaS</option><option value="customer_owned">Customer-owned deployment</option><option value="hybrid">Hybrid / transition</option></select></label>
              <label>Deployed Version<input placeholder="commit SHA, release tag, or version" value={draft.deployedVersion} onChange={event => updateDraft('deployedVersion', event.target.value)} /></label>
              <label>Release Channel<input placeholder="main, staging, IL4, etc." value={draft.releaseChannel} onChange={event => updateDraft('releaseChannel', event.target.value)} /></label>
            </div>

            <details className="admin-billing-advanced">
              <summary>
                <span>Advanced / Manual Override</span>
                <small>Use only for manual contracts, billing fixes, trial corrections, or non-Stripe exceptions.</small>
              </summary>
              <div className="admin-billing-note is-warning">
                Self-serve purchases should normally populate plan, status, billing interval, Stripe IDs, price, entitlements, and included seats through checkout/webhooks. Manual changes here can override that billing state.
                Trial, active, and past_due allow operational access. Suspended and cancelled block customer operational APIs while leaving billing recovery paths available.
              </div>
              <div className="admin-billing-grid">
                <label>Plan<select value={draft.planName} onChange={event => updateDraft('planName', event.target.value)}>{(subscription.availablePlans || []).map(plan => <option key={plan} value={plan}>{plan}</option>)}</select></label>
                <label>Access / Billing Status<select value={draft.subscriptionStatus} onChange={event => updateDraft('subscriptionStatus', event.target.value)}>{(subscription.availableStatuses || []).map(status => <option key={status} value={status}>{status}</option>)}</select><small>Suspended and cancelled block operational access for location users.</small></label>
                <label>Seat Limit<input type="number" min="1" value={draft.seatLimit} onChange={event => updateDraft('seatLimit', event.target.value)} /><small>Normally set by plan defaults plus purchased seat packs.</small></label>
                <label>Purchased Seat Packs<input type="number" min="0" value={draft.seatAddOnQuantity} onChange={event => updateDraft('seatAddOnQuantity', event.target.value)} /><small>Normally incremented by Stripe seat-pack add-on purchases.</small></label>
                <label>Paid Add-On Seats<input type="number" min="0" value={draft.seatAddOnSeats} onChange={event => updateDraft('seatAddOnSeats', event.target.value)} /><small>Useful for manual reconciliation if a seat pack was sold outside checkout.</small></label>
                <label>Purchased AI Packs<input type="number" min="0" value={draft.aiAddOnQuantity} onChange={event => updateDraft('aiAddOnQuantity', event.target.value)} /><small>Normally incremented by Stripe Afterburner AI Pack purchases.</small></label>
                <label>Paid AI Report Runs<input type="number" min="0" value={draft.aiAddOnReportRuns} onChange={event => updateDraft('aiAddOnReportRuns', event.target.value)} /></label>
                <label>Paid AI Tokens<input type="number" min="0" value={draft.aiAddOnTokens} onChange={event => updateDraft('aiAddOnTokens', event.target.value)} /></label>
                <label>Display Price / Month<input type="number" min="0" step="0.01" value={draft.pricePerMonth} onChange={event => updateDraft('pricePerMonth', event.target.value)} /></label>
                <label>Currency<input value={draft.currency} onChange={event => updateDraft('currency', event.target.value)} /></label>
                <label>Provider<input placeholder="Stripe, manual, contract, etc." value={draft.billingProvider} onChange={event => updateDraft('billingProvider', event.target.value)} /></label>
                <label>Billing Plan Key<input placeholder="standard_monthly, standard_annual_promo, premium_monthly, etc." value={draft.billingPlanKey} onChange={event => updateDraft('billingPlanKey', event.target.value)} /></label>
                <label>Billing Interval<input placeholder="monthly, annual_prepaid" value={draft.billingInterval} onChange={event => updateDraft('billingInterval', event.target.value)} /></label>
                <label>Provider Customer ID<input value={draft.providerCustomerId} onChange={event => updateDraft('providerCustomerId', event.target.value)} /></label>
                <label>Provider Subscription ID<input value={draft.providerSubscriptionId} onChange={event => updateDraft('providerSubscriptionId', event.target.value)} /></label>
                <label>Trial Ends<input type="datetime-local" value={draft.trialEndsAt} onChange={event => updateDraft('trialEndsAt', event.target.value)} /></label>
                <label>Current Period Ends<input type="datetime-local" value={draft.currentPeriodEndsAt} onChange={event => updateDraft('currentPeriodEndsAt', event.target.value)} /></label>
              </div>
            </details>

            <div className="admin-billing-subpanel">
              <div><h4>Integration Add-Ons</h4><p>Billing-controlled feature flags for this location.</p></div>
              <div className="admin-integration-bundles">
                <button type="button" onClick={() => applyIntegrationBundle('standard')}>
                  Apply Standard Bundle
                </button>
                <button type="button" onClick={() => applyIntegrationBundle('premium')}>
                  Apply Premium Bundle
                </button>
              </div>
              <div className="admin-integration-grid">
                {integrationOptions.map(option => (
                  <label key={option.key} className="admin-integration-toggle">
                    <input type="checkbox" checked={Boolean(draft.integrationFlags?.[option.key])} onChange={event => updateIntegrationFlag(option.key, event.target.checked)} />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="admin-billing-subpanel">
              <div><h4>AI Access</h4><p>The server environment selects the runtime provider first. Production GovCloud uses Amazon Bedrock through the backend AWS IAM role; local development can use OpenAI Commercial.</p></div>
              <div className="admin-ai-help">
                <strong>Current runtime:</strong>
                {subscription.runtimeAiProvider || draft.aiProvider} via {subscription.runtimeAiKeyMode || draft.aiKeyMode} ({subscription.runtimeAiConfigurationSource === 'environment' ? 'server environment' : 'location fallback'}).
                <strong>Billing intent:</strong>
                Use this section to record whether the location purchased AI features and set an optional monthly request cap. A blank or `0` cap means unlimited.
                <strong>Dev/demo exception:</strong>
                Use `openai` only for non-CUI development or demos where the backend has an approved OpenAI key configured.
                <strong>Location fallback:</strong>
                Provider and key mode below are used only when the corresponding server environment setting is absent.
              </div>
              <div className="admin-billing-grid admin-billing-grid--compact">
                <label>AI Enabled<select value={draft.aiEnabled ? 'yes' : 'no'} onChange={event => updateDraft('aiEnabled', event.target.value === 'yes')}><option value="no">No</option><option value="yes">Yes</option></select></label>
                <label>Fallback AI Provider<select value={draft.aiProvider || 'bedrock'} onChange={event => updateDraft('aiProvider', event.target.value)}><option value="bedrock">Amazon Bedrock</option><option value="openai">OpenAI Commercial</option></select></label>
                <label>Monthly AI Request Limit<input type="number" min="0" placeholder="0 = unlimited" value={draft.aiMonthlyRequestLimit} onChange={event => updateDraft('aiMonthlyRequestLimit', event.target.value)} /><small>Set to 0 for unlimited AI report generations.</small></label>
                <label>Monthly AI Token Limit<input type="number" min="0" placeholder="0 = unlimited" value={draft.aiMonthlyTokenLimit} onChange={event => updateDraft('aiMonthlyTokenLimit', event.target.value)} /><small>Set to 0 for unlimited tokens; higher or unlimited limits can be an Enterprise / Custom Contract benefit.</small></label>
                <label>Fallback Key Mode<select value={draft.aiKeyMode} onChange={event => updateDraft('aiKeyMode', event.target.value)}><option value="aws_iam_role">AWS IAM Role</option><option value="platform">Platform Key</option><option value="customer_secret_ref">Customer Secret Reference</option><option value="disabled">Disabled</option></select></label>
                <label>Secret Reference<input placeholder="aws-secretsmanager:..." value={draft.aiKeySecretRef} onChange={event => updateDraft('aiKeySecretRef', event.target.value)} /></label>
              </div>
            </div>

            <label className="admin-billing-notes">Billing / Deployment Notes<textarea rows={4} value={draft.notes} onChange={event => updateDraft('notes', event.target.value)} /></label>
            <div className="admin-billing-actions"><button type="button" onClick={saveSubscription} disabled={isSaving}>{isSaving ? 'Saving...' : 'Save Owner Billing Settings'}</button></div>
          </>
        )}
      </section>
    </>
  );

  const renderPricing = () => (
    <section className="reports-panel admin-panel admin-billing-panel admin-owner-pricing-panel">
      <div className="reports-panel__header">
        <div>
          <h3>Customer Plan Pricing</h3>
          <span>Display prices shown on customer admin and upgrade pages. Actual subscription billing is controlled by the Stripe Price IDs.</span>
        </div>
        {billingSettings ? <span className="admin-status-pill is-active">Pricing Loaded</span> : <span className="admin-status-pill is-inactive">Not Loaded</span>}
      </div>
      {!billingSettingsDraft ? (
        <p className="reports-empty">{isLoading ? 'Loading plan pricing...' : 'Plan pricing is not loaded yet.'}</p>
      ) : (
        <>
          <div className="admin-billing-note">Normal SaaS path: MFE manages the Stripe account and customers choose Standard or Premium in their location Admin page. Use the display prices below for customer-facing copy, and use Stripe Price IDs for the actual recurring billing products.</div>
          <div className="admin-billing-grid">
            <label>Standard Display Monthly Price<input type="number" min="0" step="0.01" value={billingSettingsDraft.standardMonthly} onChange={event => updateBillingSettingsDraft('standardMonthly', event.target.value)} /></label>
            <label>Standard Included Seats<input type="number" min="1" value={billingSettingsDraft.standardIncludedSeats} onChange={event => updateBillingSettingsDraft('standardIncludedSeats', event.target.value)} /></label>
            <label>Standard Annual Promo Display Monthly Equivalent<input type="number" min="0" step="0.01" value={billingSettingsDraft.standardAnnualPromoMonthly} onChange={event => updateBillingSettingsDraft('standardAnnualPromoMonthly', event.target.value)} /></label>
            <label>Standard Annual Promo Months<input type="number" min="1" value={billingSettingsDraft.standardAnnualPromoMonths} onChange={event => updateBillingSettingsDraft('standardAnnualPromoMonths', event.target.value)} /></label>
            <label>Standard Annual Promo Active<select value={billingSettingsDraft.standardAnnualPromoActive ? 'yes' : 'no'} onChange={event => updateBillingSettingsDraft('standardAnnualPromoActive', event.target.value === 'yes')}><option value="yes">Yes</option><option value="no">No</option></select></label>
            <label>Premium Display Monthly Price<input type="number" min="0" step="0.01" value={billingSettingsDraft.premiumMonthly} onChange={event => updateBillingSettingsDraft('premiumMonthly', event.target.value)} /></label>
            <label>Premium Included Seats<input type="number" min="1" value={billingSettingsDraft.premiumIncludedSeats} onChange={event => updateBillingSettingsDraft('premiumIncludedSeats', event.target.value)} /></label>
            <label>Premium Included AI Report Runs<input type="number" min="0" value={billingSettingsDraft.premiumIncludedAiReports} onChange={event => updateBillingSettingsDraft('premiumIncludedAiReports', event.target.value)} /></label>
            <label>Premium Included AI Tokens<input type="number" min="0" value={billingSettingsDraft.premiumIncludedAiTokens} onChange={event => updateBillingSettingsDraft('premiumIncludedAiTokens', event.target.value)} /></label>
            <label>Premium Annual Promo Display Monthly Equivalent<input type="number" min="0" step="0.01" value={billingSettingsDraft.premiumAnnualPromoMonthly} onChange={event => updateBillingSettingsDraft('premiumAnnualPromoMonthly', event.target.value)} /></label>
            <label>Premium Annual Promo Months<input type="number" min="1" value={billingSettingsDraft.premiumAnnualPromoMonths} onChange={event => updateBillingSettingsDraft('premiumAnnualPromoMonths', event.target.value)} /></label>
            <label>Premium Annual Promo Active<select value={billingSettingsDraft.premiumAnnualPromoActive ? 'yes' : 'no'} onChange={event => updateBillingSettingsDraft('premiumAnnualPromoActive', event.target.value === 'yes')}><option value="yes">Yes</option><option value="no">No</option></select></label>
            <label>A La Carte Seat Pack Size<input type="number" min="1" value={billingSettingsDraft.seatPackSize} onChange={event => updateBillingSettingsDraft('seatPackSize', event.target.value)} /></label>
            <label>A La Carte Seat Pack Display Monthly Price<input type="number" min="0" step="0.01" value={billingSettingsDraft.seatPackMonthly} onChange={event => updateBillingSettingsDraft('seatPackMonthly', event.target.value)} /></label>
            <label>Afterburner AI Pack Report Runs<input type="number" min="1" value={billingSettingsDraft.aiPackReportRuns} onChange={event => updateBillingSettingsDraft('aiPackReportRuns', event.target.value)} /></label>
            <label>Afterburner AI Pack Tokens<input type="number" min="1" value={billingSettingsDraft.aiPackTokens} onChange={event => updateBillingSettingsDraft('aiPackTokens', event.target.value)} /></label>
            <label>Afterburner AI Pack Display Monthly Price<input type="number" min="0" step="0.01" value={billingSettingsDraft.aiPackMonthly} onChange={event => updateBillingSettingsDraft('aiPackMonthly', event.target.value)} /></label>
            <label>SMS Dispatch Pack Placeholder Price<input type="number" min="0" step="0.01" value={billingSettingsDraft.smsDispatchPackMonthly} onChange={event => updateBillingSettingsDraft('smsDispatchPackMonthly', event.target.value)} /><small>Hidden from upgrade pages until enabled.</small></label>
            <label>SMS Dispatch Pack Visible<select value={billingSettingsDraft.smsDispatchPackVisible ? 'yes' : 'no'} onChange={event => updateBillingSettingsDraft('smsDispatchPackVisible', event.target.value === 'yes')}><option value="no">No</option><option value="yes">Yes</option></select></label>
            <label>Recovery Ops Pack Placeholder Price<input type="number" min="0" step="0.01" value={billingSettingsDraft.recoveryOpsPackMonthly} onChange={event => updateBillingSettingsDraft('recoveryOpsPackMonthly', event.target.value)} /><small>Hidden from upgrade pages until enabled.</small></label>
            <label>Recovery Ops Pack Visible<select value={billingSettingsDraft.recoveryOpsPackVisible ? 'yes' : 'no'} onChange={event => updateBillingSettingsDraft('recoveryOpsPackVisible', event.target.value === 'yes')}><option value="no">No</option><option value="yes">Yes</option></select></label>
            <label>Standard Stripe Price ID<input value={billingSettingsDraft.standardStripeMonthlyPriceId} onChange={event => updateBillingSettingsDraft('standardStripeMonthlyPriceId', event.target.value)} /></label>
            <label>Standard Annual Promo Stripe Price ID<input value={billingSettingsDraft.standardStripeAnnualPromoPriceId} onChange={event => updateBillingSettingsDraft('standardStripeAnnualPromoPriceId', event.target.value)} /></label>
            <label>Premium Stripe Price ID<input value={billingSettingsDraft.premiumStripeMonthlyPriceId} onChange={event => updateBillingSettingsDraft('premiumStripeMonthlyPriceId', event.target.value)} /></label>
            <label>Premium Annual Promo Stripe Price ID<input value={billingSettingsDraft.premiumStripeAnnualPromoPriceId} onChange={event => updateBillingSettingsDraft('premiumStripeAnnualPromoPriceId', event.target.value)} /></label>
            <label>A La Carte Seat Pack Stripe Price ID<input value={billingSettingsDraft.seatPackStripeMonthlyPriceId} onChange={event => updateBillingSettingsDraft('seatPackStripeMonthlyPriceId', event.target.value)} /></label>
            <label>Afterburner AI Pack Stripe Price ID<input value={billingSettingsDraft.aiPackStripeMonthlyPriceId} onChange={event => updateBillingSettingsDraft('aiPackStripeMonthlyPriceId', event.target.value)} /></label>
            <label>SMS Dispatch Pack Stripe Price ID<input value={billingSettingsDraft.smsDispatchPackStripeMonthlyPriceId} onChange={event => updateBillingSettingsDraft('smsDispatchPackStripeMonthlyPriceId', event.target.value)} /></label>
            <label>Recovery Ops Pack Stripe Price ID<input value={billingSettingsDraft.recoveryOpsPackStripeMonthlyPriceId} onChange={event => updateBillingSettingsDraft('recoveryOpsPackStripeMonthlyPriceId', event.target.value)} /></label>
            <label>Checkout Success URL<input value={billingSettingsDraft.stripeSuccessUrl} onChange={event => updateBillingSettingsDraft('stripeSuccessUrl', event.target.value)} /></label>
            <label>Checkout Cancel URL<input value={billingSettingsDraft.stripeCancelUrl} onChange={event => updateBillingSettingsDraft('stripeCancelUrl', event.target.value)} /></label>
            <label>Billing Portal Return URL<input value={billingSettingsDraft.stripePortalReturnUrl} onChange={event => updateBillingSettingsDraft('stripePortalReturnUrl', event.target.value)} /></label>
          </div>
          <div className="admin-billing-actions"><button type="button" onClick={saveBillingSettings} disabled={isSaving}>{isSaving ? 'Saving...' : 'Save Customer Plan Pricing'}</button></div>
        </>
      )}
    </section>
  );

  const renderAnnouncements = () => (
    <section className="reports-panel admin-panel admin-billing-panel admin-owner-announcements-panel">
      <div className="reports-panel__header">
        <div>
          <h3>Platform Announcements</h3>
          <span>Broadcast system updates, maintenance windows, and location-specific notices inside the app.</span>
        </div>
        <span className="admin-status-pill is-active">{announcements.filter(row => row.isActive).length} Active</span>
      </div>
      <div className="admin-billing-grid">
        <label>Title<input value={announcementDraft.title} onChange={event => updateAnnouncementDraft('title', event.target.value)} /></label>
        <label>Severity<select value={announcementDraft.severity} onChange={event => updateAnnouncementDraft('severity', event.target.value)}><option value="info">Info</option><option value="success">Success</option><option value="warning">Warning</option><option value="critical">Critical</option></select></label>
        <label>Audience<select value={announcementDraft.audienceScope} onChange={event => updateAnnouncementDraft('audienceScope', event.target.value)}><option value="all_locations">All locations</option><option value="location">One location</option><option value="role">One role</option></select></label>
        <label>Active<select value={announcementDraft.isActive ? 'yes' : 'no'} onChange={event => updateAnnouncementDraft('isActive', event.target.value === 'yes')}><option value="yes">Yes</option><option value="no">No</option></select></label>
        {announcementDraft.audienceScope === 'location' ? <label>Location<select value={announcementDraft.location} onChange={event => updateAnnouncementDraft('location', event.target.value)}><option value="">Select location</option>{rows.map(row => <option key={row.location} value={row.location}>{row.location}</option>)}</select></label> : null}
        {announcementDraft.audienceScope === 'role' ? <label>Role<input placeholder="Commander, siteAdmin, Technician..." value={announcementDraft.role} onChange={event => updateAnnouncementDraft('role', event.target.value)} /></label> : null}
        <label>Starts At<input type="datetime-local" value={announcementDraft.startsAt} onChange={event => updateAnnouncementDraft('startsAt', event.target.value)} /></label>
        <label>Ends At<input type="datetime-local" value={announcementDraft.endsAt} onChange={event => updateAnnouncementDraft('endsAt', event.target.value)} /></label>
      </div>
      <label className="admin-billing-notes">Message<textarea rows={3} value={announcementDraft.body} onChange={event => updateAnnouncementDraft('body', event.target.value)} /></label>
      <div className="admin-billing-actions">
        {editingAnnouncementId ? <button type="button" className="profile-modal__secondary" onClick={() => { setEditingAnnouncementId(''); setAnnouncementDraft({ ...emptyAnnouncementDraft }); }}>Cancel Edit</button> : null}
        <button type="button" onClick={saveAnnouncement} disabled={isSaving}>{isSaving ? 'Saving...' : editingAnnouncementId ? 'Save Announcement' : 'Create Announcement'}</button>
      </div>
      <div className="reports-table-shell">
        <table className="reports-table admin-owner-announcements-table">
          <thead><tr><th>Title</th><th>Audience</th><th>Severity</th><th>Status</th><th>Window</th><th>Created By</th><th>Actions</th></tr></thead>
          <tbody>
            {announcements.length > 0 ? announcements.map(row => (
              <tr key={row.id}>
                <td><strong>{row.title}</strong><span>{row.body}</span></td>
                <td>{row.audienceScope === 'location' ? row.location : row.audienceScope === 'role' ? row.role : 'All locations'}</td>
                <td><span className={`platform-announcement-pill is-${row.severity}`}>{row.severity}</span></td>
                <td>{row.isActive ? 'Active' : 'Inactive'}</td>
                <td>{formatLocalDateTime(row.startsAt) || 'Now'} - {formatLocalDateTime(row.endsAt) || 'No end'}</td>
                <td>{row.createdBy || '--'}</td>
                <td><button type="button" className="admin-owner-action" onClick={() => { setEditingAnnouncementId(row.id); setAnnouncementDraft(hydrateAnnouncementDraft(row)); }}>Edit</button></td>
              </tr>
            )) : <tr><td colSpan={7} className="reports-empty">No platform announcements yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );

  return (
    <div className="admin-tab">
      <header className="admin-hero owner-portal-hero">
        <div>
          <div className="reports-eyebrow">MFE Platform Owner</div>
          <h2>Platform Owner Portal</h2>
          <p>Manage customer accounts, location contracts, billing, entitlements, support contacts, deployment mode, and renewal tracking.</p>
        </div>
        <button type="button" className="admin-refresh-button" onClick={() => loadPortal(selectedLocation)} disabled={isLoading}>
          {isLoading ? 'Refreshing...' : 'Refresh Owner Portal'}
        </button>
      </header>

      {message ? <div className="admin-message is-success">{message}</div> : null}
      {error ? <div className="admin-message is-error">{error}</div> : null}

      <div className="owner-portal-layout">
        <aside className="admin-side-menu owner-portal-side-menu" aria-label="MFE owner portal sections">
          <button type="button" className={activeSection === 'locations' ? 'is-active' : ''} onClick={() => setActiveSection('locations')}>
            <span>Location Billing Console</span>
            <small>Locations, subscriptions, detail editor</small>
          </button>
          <button type="button" className={activeSection === 'pricing' ? 'is-active' : ''} onClick={() => setActiveSection('pricing')}>
            <span>Customer Pricing Plan</span>
            <small>Standard, Premium, Stripe plan settings</small>
          </button>
          <button type="button" className={activeSection === 'ownerMetrics' ? 'is-active' : ''} onClick={() => setActiveSection('ownerMetrics')}>
            <span>MFE Owner Report</span>
            <small>Usage, MRR, add-ons</small>
          </button>
          <button type="button" className={activeSection === 'accountAccess' ? 'is-active' : ''} onClick={() => setActiveSection('accountAccess')}>
            <span>Account Access Controls</span>
            <small>Registered users, suspensions, blocks</small>
          </button>
          <button type="button" className={activeSection === 'announcements' ? 'is-active' : ''} onClick={() => setActiveSection('announcements')}>
            <span>Platform Announcements</span>
            <small>Broadcast notices and maintenance banners</small>
          </button>
          <button type="button" className={activeSection === 'superusers' ? 'is-active' : ''} onClick={() => setActiveSection('superusers')}>
            <span>Platform Superusers</span>
            <small>Read-only owner access list</small>
          </button>
        </aside>
        <div className="owner-portal-content">
          {activeSection === 'locations' ? renderLocationConsole() : null}
          {activeSection === 'pricing' ? renderPricing() : null}
          {activeSection === 'ownerMetrics' ? renderOwnerMetrics() : null}
          {activeSection === 'accountAccess' ? renderAccountAccess() : null}
          {activeSection === 'announcements' ? renderAnnouncements() : null}
          {activeSection === 'superusers' ? renderSuperusers() : null}
        </div>
      </div>
    </div>
  );
}
